import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import {
  Menu,
  PlusCircle,
  Inspect,
  Contact,
  LogOut,
  Activity,
  Sparkles,
  AtSign,
  Code2,
  Landmark,
  LayoutDashboard,
  Users,
  UserCircle2
} from "lucide-react"
import { useAppDispatch, useAppSelector, useLogout } from "../../hooks"
import { useAssistantTopicsBadge } from "../../hooks/use-assistant-topics-badge"
import {
  deleteMasquerade,
  masquerade,
  selectProfileUpdated,
  selectUserDetails
} from "../../../redux/slices/auth"
import { checkCanUseAssistant, checkIsAdmin, hasUserRole } from "../../utils"
import { AddNewDropdownItem, PanelType } from "../../../types"
import {
  selectVisiblePanel,
  setVisiblePanel,
  setStaffAiDrawerOpen
} from "../../../redux/slices/app"
import {
  Dropdown,
  PanelsGroup,
  ImageWrapper,
  PremiumFeaturesIcon,
  SearchAnything,
  ScreenReaderOnly,
  RoundedImg
} from "../../ui"
import BlockingLoader from "../../ui/blocking-loader"

type Props = {
  setSidebarOpen: (sidebarOpen: boolean) => void
}

const Header = ({ setSidebarOpen }: Props) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  // The drawer is redundant on the full-page assistant
  const onStaffAiPage = location.pathname.startsWith("/staff-ai")
  const handleLogout = useLogout()

  const user = useAppSelector(selectUserDetails)
  const profileUpdated = useAppSelector(selectProfileUpdated)
  const isMasq = user?.is_masq && user.is_masq !== "0"
  const isAdmin = checkIsAdmin(user?.roles ?? [])
  const canUseAssistant = checkCanUseAssistant(user?.roles ?? [])
  const topicsBadge = useAssistantTopicsBadge(canUseAssistant)

  const [imgVersion, setImgVersion] = useState(0)

  useEffect(() => {
    if (profileUpdated) {
      setImgVersion(prev => prev + 1)
    }
  }, [profileUpdated])

  const visiblePanel = useAppSelector(selectVisiblePanel)

  // Both calls take several seconds and the menu closes on click
  const [pendingAction, setPendingAction] = useState<
    "switch-back" | "logout" | null
  >(null)

  const delMasquerade = useCallback(async () => {
    setPendingAction("switch-back")
    try {
      await dispatch(deleteMasquerade())
    } finally {
      setPendingAction(null)
    }
  }, [dispatch])

  // No reset on success: logout clears the user, which unmounts the header
  const onLogout = useCallback(async () => {
    setPendingAction("logout")
    try {
      await handleLogout()
    } catch (error) {
      setPendingAction(null)
      throw error
    }
  }, [handleLogout])

  const handleMasquerade = (loid: string) => {
    dispatch(masquerade(loid))
  }

  const delMasqItem = useMemo(
    () => ({
      name: "Switch Back",
      href: "#",
      icon: Inspect,
      onClick: delMasquerade
    }),
    [delMasquerade]
  )

  const openPanel = useCallback(
    (panelType: PanelType) => {
      dispatch(setVisiblePanel(panelType))
    },
    [dispatch]
  )

  const profileDropdownItems = useMemo(
    () => [
      { name: "My account", href: "/account", icon: Contact },
      { name: "Logout", onClick: onLogout, icon: LogOut }
    ],
    [onLogout]
  )

  const profileItems = useMemo(
    () =>
      isMasq ? [delMasqItem, ...profileDropdownItems] : profileDropdownItems,
    [delMasqItem, isMasq, profileDropdownItems]
  )

  const addNewDropdownItems: AddNewDropdownItem[] = useMemo(
    () => [
      {
        name: "Customer",
        icon: LayoutDashboard,
        requiredRole: "administrator",
        onClick: () => openPanel("organization/new")
      },
      {
        name: "Brand",
        icon: Landmark,
        requiredRole: "bank admin",
        onClick: () => openPanel("brand/new")
      },
      {
        name: "Loan officer",
        icon: Users,
        requiredRole: "bank admin",
        onClick: () => openPanel("loan-officer/new")
      },
      {
        name: "Integration",
        icon: Code2,
        requiredRole: "bank admin",
        onClick: () => navigate("/integrations/new")
      },
      {
        name: "Engagement",
        icon: AtSign,
        requiredRole: "loan officer",
        onClick: () => openPanel("engagement/new")
      }
    ],
    [openPanel, navigate]
  )

  const newDropdownItems = useMemo(
    () =>
      addNewDropdownItems.filter(item =>
        hasUserRole(user?.roles ?? [], item.requiredRole)
      ),
    [addNewDropdownItems, user?.roles]
  )

  if (!user) return null

  return (
    <div className="sticky top-0 z-40 flex-shrink-0 flex h-16 bg-white shadow">
      <button
        type="button"
        className="px-4 border-r border-gray-200 text-gray-500 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden"
        onClick={() => setSidebarOpen(true)}
      >
        <ScreenReaderOnly>Open sidebar</ScreenReaderOnly>
        <Menu size={20} aria-hidden="true" />
      </button>
      <div className="flex-1 px-4 flex justify-between">
        <div className="flex-1 flex">
          <SearchAnything
            isAdmin={isAdmin}
            handleMasquerade={handleMasquerade}
          />
        </div>
        <div className="ml-2 lg:ml-6 flex items-center gap-4 lg:gap-6">
          {isAdmin && (
            <Link to="/account/feature-addons">
              <div className="flex items-center text-amber-600">
                <PremiumFeaturesIcon size={16} className="lg:mr-2" />
                <span className="hidden lg:inline">Premium Features</span>
              </div>
            </Link>
          )}
          <Link to="/recent-activity">
            <div className="flex items-center text-violet-600">
              <Activity size={20} className="lg:mr-2" />
              <span className="hidden lg:inline">Recent Activity</span>
            </div>
          </Link>
          {canUseAssistant && (
            <button
              type="button"
              disabled={onStaffAiPage}
              onClick={() => dispatch(setStaffAiDrawerOpen(true))}
              title={
                onStaffAiPage
                  ? "You're on the AI Assistant page"
                  : topicsBadge.hasNew
                  ? `New to explore: ${
                      topicsBadge.topics[0] ?? "updates since your last look"
                    }`
                  : "AI Assistant"
              }
              className="relative rounded-full border border-violet-900 p-1.5 text-violet-900 transition-colors hover:bg-violet-50 disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent"
            >
              <ScreenReaderOnly>
                {topicsBadge.hasNew
                  ? "Open AI Assistant — new topics to explore"
                  : "Open AI Assistant"}
              </ScreenReaderOnly>
              <Sparkles size={16} aria-hidden="true" />
              {topicsBadge.hasNew && !onStaffAiPage && (
                <span
                  aria-hidden="true"
                  className="absolute -right-0.5 -top-0.5 flex h-2.5 w-2.5"
                >
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-violet-500 opacity-75 motion-reduce:hidden" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full border border-white bg-violet-600" />
                </span>
              )}
            </button>
          )}
          <Dropdown
            items={newDropdownItems}
            menuBtn={
              <div className="flex items-center">
                <PlusCircle size={20} className="mr-2" />
                <span className="hidden lg:inline">Add New</span>
              </div>
            }
          />

          <Dropdown
            items={profileItems}
            menuBtn={
              <>
                <ScreenReaderOnly>Open user menu</ScreenReaderOnly>
                {user.picture ? (
                  <ImageWrapper>
                    <RoundedImg
                      size={8}
                      src={`${user.picture}?v=${imgVersion}`}
                      alt="user thumbnail"
                    />
                  </ImageWrapper>
                ) : (
                  <UserCircle2 size="32" aria-hidden="true" />
                )}
              </>
            }
          />
        </div>
      </div>

      <PanelsGroup visiblePanel={visiblePanel} />

      {pendingAction && (
        <BlockingLoader
          label={
            pendingAction === "logout" ? "Logging out…" : "Switching back…"
          }
        />
      )}
    </div>
  )
}
export default Header
